import {
  Box,
  Center,
  Flex,
  Grid,
  HStack,
  Icon,
  Progress,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { FileUrlsProvider } from '@duke-hq/svc-files-ui/useTiptapFileSrcResolver';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { Trans } from '@lingui/react/macro';
import { type ReactNode, Suspense } from 'react';
import { AlertCircle } from 'lucide-react';

import { AssessmentMetadata } from './AssessmentMetadata';
import { AssessmentUserHeader } from './AssessmentUserHeader';
import { SlideContent } from '../components/SlideContent';
import { SlideQuestion } from '../components/SlideQuestion';
import { TableOfContents } from './TableOfContents';
import { NavControls } from './NavControls';
import { QuestionResult } from './QuestionResult';
import { Overview } from './Overview';
import { MarkingProvider, useMarkingContext } from './context/MarkingContext';
import type { AssessmentSubmission } from './types';

function ErrorState() {
  return (
    <Center h="100vh">
      <VStack spacing={4}>
        <Icon as={AlertCircle} boxSize={12} color="fg.muted" />
        <Text fontSize="lg" fontWeight="medium">
          <Trans>Something went wrong</Trans>
        </Text>
      </VStack>
    </Center>
  );
}

function EmptySubmission({
  assessmentSubmission,
  drawerActions,
}: {
  assessmentSubmission: AssessmentSubmission;
  drawerActions?: ReactNode;
}) {
  return (
    <Flex height="100vh" direction="column" justify="space-between" flex="1">
      <AssessmentUserHeader
        user={assessmentSubmission.unitAssessmentEnrolment.user}
        assessmentSubmission={assessmentSubmission}
        drawerActions={drawerActions}
      />

      <AssessmentMetadata assessmentSubmission={assessmentSubmission} />

      <Center flex="1">
        <VStack spacing={4}>
          <Icon as={AlertCircle} boxSize={12} color="fg.muted" />
          <Text fontSize="lg" fontWeight="medium">
            <Trans>No Answers Submitted</Trans>
          </Text>
          <Text color="fg.muted" textAlign="center" maxW="md">
            <Trans>
              This assessment has no submitted answers. Please contact support if this issue
              persists.
            </Trans>
          </Text>
        </VStack>
      </Center>
    </Flex>
  );
}

export function Details({
  assessmentId,
  drawerActions,
}: {
  assessmentId: string;
  drawerActions?: ReactNode;
}) {
  const [assessmentSubmission] = trpcAdmin.assessments.getById.useSuspenseQuery(
    parseInt(assessmentId),
  );

  if (!assessmentSubmission || assessmentSubmission.unitAssessmentVersionId === null) {
    return <ErrorState />;
  }

  if (assessmentSubmission.assessorSlides.length === 0) {
    return (
      <EmptySubmission assessmentSubmission={assessmentSubmission} drawerActions={drawerActions} />
    );
  }

  return (
    <FileUrlsProvider fileUrls={assessmentSubmission.fileUrls}>
      <MarkingProvider assessmentId={assessmentId} assessmentSubmission={assessmentSubmission}>
        <MarkingLayout drawerActions={drawerActions} />
      </MarkingProvider>
    </FileUrlsProvider>
  );
}

function MarkingLayout({ drawerActions }: { drawerActions?: ReactNode }) {
  const {
    assessmentSubmission,
    currentSlide,
    currentAnswer,
    isOverviewScreen,
    progress,
    answerInput,
    selectedAnswerId,
    setSelectedAnswerId,
    handleDownloadFile,
    getSubmissionFileUrl,
  } = useMarkingContext();

  if (currentAnswer === undefined) {
    return null;
  }

  return (
    <Flex height="100vh" direction="column" justify="space-between" flex="1">
      <AssessmentUserHeader
        user={assessmentSubmission.unitAssessmentEnrolment.user}
        assessmentSubmission={assessmentSubmission}
        drawerActions={drawerActions}
      />

      <AssessmentMetadata assessmentSubmission={assessmentSubmission} />

      <Box bg="bg.muted" py={2} px={6} w="100%" position="sticky" top="0" zIndex={1}>
        <HStack justify="space-between" align="center" gap={4}>
          <TableOfContents />
          <NavControls />
        </HStack>
      </Box>

      <Flex direction="column" flex="1" minH={0}>
        <Suspense fallback={<Spinner />}>
          {!isOverviewScreen && currentSlide ? (
            <Grid
              gridTemplateColumns="1fr minmax(440px, 33%)"
              gridTemplateRows="minmax(0, 1fr)"
              flex="1"
              minH={0}
            >
              <Box padding="8" minWidth="0" minH={0} overflowY="auto" bg="bg.surface">
                <Progress value={progress} mb={7} h={2} hasStripe />

                <Text fontWeight="semibold" fontSize="2xl" mb={4}>
                  {currentSlide.title}
                </Text>

                <SlideContent slide={currentSlide} />

                {currentSlide.question ? (
                  <SlideQuestion
                    slide={currentSlide}
                    currentQuestion={currentSlide.question}
                    setSelectedAnswerId={setSelectedAnswerId}
                    answerInput={answerInput}
                    selectedAnswerId={selectedAnswerId}
                    isReadOnly
                    assessmentMode={true}
                    downloadFile={handleDownloadFile}
                    getFileUrl={getSubmissionFileUrl}
                    assessmentSubmissionId={assessmentSubmission.id}
                    assessedAt={assessmentSubmission.assessed_at}
                  />
                ) : null}
              </Box>
              <Box
                backgroundColor="bg.subtle"
                padding="0"
                minH={0}
                overflowY="auto"
                borderLeftWidth="1px"
                borderColor="border.default"
              >
                <QuestionResult slide={currentSlide} answer={currentAnswer} />
              </Box>
            </Grid>
          ) : (
            <Box flex="1" minH={0} overflowY="auto">
              <Overview />
            </Box>
          )}
        </Suspense>
      </Flex>
    </Flex>
  );
}
